import {
	ActionIcon,
	Anchor,
	Button,
	Container,
	Image,
	Input,
	MultiSelect,
	Pagination,
	Paper,
	PasswordInput,
	Pill,
	RingProgress,
	Select,
	Tabs,
	Text,
	TextInput,
	Title,
	UnstyledButton,
	createTheme,
	rem,
	px
} from "@mantine/core"
import type { MantineThemeOverride } from "@mantine/core"
import { DateInput } from "@mantine/dates"

import { IconCaretDownFilled } from "@tabler/icons-react"
import actionIconStyles from "./styles/ActionIcon.module.css"
import anchorStyles from "./styles/Anchor.module.css"
import buttonStyles from "./styles/Button.module.css"
import checkBoxkStyle from "./styles/Checkbox.module.css"
import dateInputStyles from "./styles/DateInput.module.css"
import imageStyles from "./styles/Image.module.css"
import inputStyles from "./styles/Input.module.css"
import multiSelectStyles from "./styles/MultiSelect.module.css"
import paginationStyles from "./styles/Pagination.module.css"
import passwordInputStyles from "./styles/PasswordInput.module.css"
import pillStyles from "./styles/Pill.module.css"
import selectStyles from "./styles/Select.module.css"
import textStyles from "./styles/Text.module.css"
import titleStyles from "./styles/Title.module.css"
import unstyledButtonStyles from "./styles/UnstyledButton.module.css"
import focusStyles from "./styles/focus.module.css"

const CONTAINER_SIZES: Record<string, string> = {
	xxs: rem(300),
	xs: rem(400),
	sm: rem(500),
	md: rem(800),
	lg: rem(1256),
	xl: rem(1440),
	xxl: rem(1920)
}

export const theme: MantineThemeOverride = createTheme({
	colors: {
		royal: [
			"#021E3E", // 0
			"#ACE3F7", // 1
			"#2F6BB3", // 2
			"#E87436", // 3
			"#3E474A", // 4
			"#051557", // 5
			"#FFFFFF", // 6
			"#B4CFFA", // 7
			"#004ECB", // 8
			"#052396", // 9
			"#F0F5F9", // 10
			"#173E8D", // 11
			"#CBD7FF", // 12
			"#A8C8CF", // 13
			"#1E2746", // 14
			"#11162A", // 15
			"#181F39", // 16
			"#263053", // 17
			"#77FFFF", // 18
			"#F5F5F5", // 19
			"#D1D2D5", // 20
			"#4F4F4F", // 21
			"#E9F4FF", // 22
			"#3F4B7F", // 23
			"#E0E0E0", // 24
			"#EBEEF7", // 25
			"#4F4F4F", // 26
			"#828282", // 27
			"#11162B", // 28
			"#E7E7E7", // 29
			"#0273BA", // 30
			"#E8E8E8", // 31
			"#FFC7C7", // 32
			"#172FBB", // 33
			"#D4DAF7", // 34
			"#ECEFFE", // 35
			"#64B5F6", // 36
			"#1E88E5", // 37
			"#1565C0", // 38
			"#124E84", // 39
			"#C9DAF6", // 40
			"#B4B9CD", // 41
			"#EEEEEE", // 42
			"#BDBDBD", // 43
			"#424242", // 44
			"#C74700", // 45
			"#FFD7D7", // 46
			"#E0F7FF", // 47
			"#EBFFEE", // 48
			"#02542D", // 49
			"#FFF1C2", // 50
			"#401B01", // 51
			"#C92A2A", // 52
			"#AFF4C6", // 53
			"#FFE082", // 54
			"#FF8F00", // 55
			"#39BCD0", //56
			"#004DCC", //57
			"#FFBDBD", //58
			"#C74700", //59
			"#98A3CF", //60
			"#EEF0F8", //61
			"#14AE5C", //62
			"#8FC998", //63
			"#C55E62", //64
			"#FEFCFC", //65
			"#FEFBE5", //66
			"#B4800B", //67
			"#E1AC26", //68
			"#E2E2E4", //69
			"#B3B1B8", //70
			"#AAAAAA", //71
			"#44559E", //72
			"#4EB094", //73
			"#FF000D", //74
			"#00E6FF", //75
			"#FFFFFF1A", //76 - 10%
			"#FFFFFF26", //77 - 15%
			"#FFFFFF33", //78 - 20%
			"#FFFFFF4D", //79 - 30%
			"#FFFFFF66", //80 - 40%
			"#FFFFFF80", //81 - 50%
			"#FFFFFFB3", //82 - 70%
			"#FFFFFFCC", //83 - 80%
			"#FFFFFFE6", //84 - 90%
			"#2196F34D", //85 - light (30%)
			"#2196F3CC", //86 - medium (80%)
			"#2196F3E6", //87 - strong (90%)
			"#F4433633", //88 - bg light (20%)
			"#F443364D", //89 - bg medium (30%)
			"#F4433680", //90 - border medium (50%)
			"#F44336CC", //91 - border strong (80%)
			"#F44336E6", //92 - bg strong (90%)
			"#B42318", //93 - error red
			"#D35D5E", //94
			"#B4B4B4", //95
			"#900B09", //96
			"#048112", //97
			"#121217", //98
			"#1D1D22", //99
			"#242429", //100
			"#F9F9F9", //101
			"#5A6E96", // 102
			"#EE0004", //103
			"#0000002B", //104
			"#031F94", //105
			"#E6EFF6", //106
			"#6A6560", //107
			"#9CA3AF", //108
			"#2E2E2E", //109
			"#485483", //110 - scrollbar
			"#B42318", //111 - error red
			"#BEEDFB", //112 - light blue background
			"#FEE9E7", // 113 - light  red bg
			"#C82A27", // 114 - dark red
			"#B9BBBC4D", // 115 - dining table border
			"#021e3eeb", // 116 - modal overlay
			"#003D73", // 117 - search result divider
			"#223ba2", // 118 - badge dark blue for dining print pdf
			"#B3B3B3", // 119 - character count limit color
			"#583619", // 120 - brown dark
			"#fff1c2", // 121 - light yellow
			"#DEE6FF", // 122 - lightest blue
			"#AFB6CF", // 123 - grayish blue
			"#F6F8FE", // 124 - table body background
			"#B9C1D9", // 125 - empty message colors
			"#D8DAE4" // 126 - light gray for svg
		],
		translucentGray: [
			"rgba(248, 249, 250, 0.5)",
			"rgba(241, 243, 245, 0.5)",
			"rgba(233, 236, 239, 0.5)",
			"rgba(222, 226, 230, 0.5)",
			"rgba(206, 212, 218, 0.5)",
			"rgba(173, 181, 189, 0.5)",
			"rgba(134, 142, 150, 0.5)",
			"rgba(73, 80, 87, 0.5)",
			"rgba(52, 58, 64, 0.5)",
			"rgba(33, 37, 41, 0.5)",
			"rgba(1, 30, 62, 0.23)",
			"rgba(0, 0, 0, 0.17)"
		]
	},
	cursorType: "pointer",
	primaryColor: "royal",
	primaryShade: 9,
	fontFamily: "Poppins, sans-serif",
	fontSizes: {
		xs: rem(10),
		sm: rem(12),
		md: rem(14),
		lg: rem(16),
		xl: rem(20),
		// Custom font sizes for GD
		xxs: rem(13),
		lgBody: rem(18),
		h5: rem(24),
		xxl: rem(36)
	},
	headings: {
		fontFamily: "Poppins, sans-serif",
		fontWeight: "600",

		// Individual heading properties
		sizes: {
			h3: {
				fontSize: rem(40)
			},
			h4: {
				fontSize: rem(36)
			},
			h5: {
				fontSize: rem(24)
			},
			h6: {
				fontSize: rem(16)
			}
		}
	},
	radius: {
		infoCard: "calc(4.625rem * var(--mantine-scale))",
		larger: "calc(1.5rem * var(--mantine-scale))",
		// Custom radius for GD
		xxl: "calc(1.5rem * var(--mantine-scale))", // 24px
		pill: "calc(4rem * var(--mantine-scale))" // 64px
	},
	components: {
		Button: Button.extend({
			classNames: buttonStyles,
			defaultProps: {
				radius: "xl"
			}
		}),
		Image: Image.extend({
			classNames: {
				root: imageStyles.root
			}
		}),
		Input: Input.extend({
			classNames: inputStyles
		}),
		TextInput: TextInput.extend({
			classNames: inputStyles
		}),
		Paper: Paper.extend({
			defaultProps: {
				radius: "larger"
			}
		}),
		RingProgress: RingProgress.extend({
			defaultProps: {
				roundCaps: true,
				rootColor: "white"
			}
		}),
		Select: Select.extend({
			classNames: selectStyles,
			defaultProps: {
				role: "combobox",
				rightSection: <IconCaretDownFilled fill="var(--text-color-primary)" />,
				rightSectionPointerEvents: "none",
				withCheckIcon: false,
				allowDeselect: false,
				withScrollArea: false
			}
		}),
		MultiSelect: MultiSelect.extend({
			classNames: multiSelectStyles,
			defaultProps: {
				rightSection: <IconCaretDownFilled fill="var(--text-color-primary)" />,
				rightSectionPointerEvents: "none"
			}
		}),
		Title: Title.extend({
			classNames: {
				root: titleStyles.root
			},
			styles: {
				root: {
					padding: 0
				}
			}
		}),
		Text: Text.extend({
			classNames: textStyles,
			styles: {
				root: {
					maxWidth: 919
				}
			}
		}),
		DateInput: DateInput.extend({
			classNames: dateInputStyles
		}),
		PasswordInput: PasswordInput.extend({
			classNames: passwordInputStyles
		}),
		Pill: Pill.extend({
			classNames: pillStyles
		}),
		UnstyledButton: UnstyledButton.extend({
			classNames: unstyledButtonStyles
		}),
		Tabs: Tabs.extend({
			defaultProps: {}
		}),
		ActionIcon: ActionIcon.extend({
			classNames: actionIconStyles
		}),
		Container: Container.extend({
			vars: (_, { size, fluid }) => ({
				root: {
					"--container-size": fluid
						? "100%"
						: size !== undefined && size in CONTAINER_SIZES
							? CONTAINER_SIZES[size]
							: rem(size)
				}
			})
		}),
		Checkbox: ActionIcon.extend({
			classNames: checkBoxkStyle
		}),
		Pagination: Pagination.extend({
			classNames: paginationStyles
		}),
		Anchor: Anchor.extend({
			classNames: anchorStyles
		})
	},
	spacing: {
		xs: "0.625rem",
		sm: "0.75rem",
		md: "1rem",
		lg: "1.25rem",
		xl: "1.5rem",
		// Custom spacing for GD
		xxl: "1.75rem", // 28px
		"3xl": "2rem", // 32px
		"4xl": "2.5rem", // 40px
		"5xl": "3.5rem", // 56px
		"6xl": "3.75rem" // 60px
	},
	focusClassName: focusStyles.focus,
	shadows: {
		xs: "0 calc(0.0625rem * var(--mantine-scale)) calc(0.1875rem * var(--mantine-scale)) rgba(0, 0, 0, 0.05)",
		sm: "0 calc(0.0625rem * var(--mantine-scale)) calc(0.1875rem * var(--mantine-scale)) rgba(0, 0, 0, 0.05), 0 calc(0.0625rem * var(--mantine-scale)) calc(0.125rem * var(--mantine-scale)) rgba(0, 0, 0, 0.1)",
		md: "0 calc(0.0625rem * var(--mantine-scale)) calc(0.1875rem * var(--mantine-scale)) rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0 calc(0.625rem * var(--mantine-scale)) calc(0.9375rem * var(--mantine-scale)) calc(-0.3125rem * var(--mantine-scale)), rgba(0, 0, 0, 0.04) 0 calc(0.4375rem * var(--mantine-scale)) calc(0.4375rem * var(--mantine-scale)) calc(-0.3125rem * var(--mantine-scale))",
		lg: "0 calc(0.0625rem * var(--mantine-scale)) calc(0.1875rem * var(--mantine-scale)) rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0 calc(1.25rem * var(--mantine-scale)) calc(1.5625rem * var(--mantine-scale)) calc(-0.3125rem * var(--mantine-scale)), rgba(0, 0, 0, 0.04) 0 calc(0.625rem * var(--mantine-scale)) calc(0.625rem * var(--mantine-scale)) calc(-0.3125rem * var(--mantine-scale))",
		xl: "0 calc(0.0625rem * var(--mantine-scale)) calc(0.1875rem * var(--mantine-scale)) rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0 calc(1.75rem * var(--mantine-scale)) calc(1.4375rem * var(--mantine-scale)) calc(-0.4375rem * var(--mantine-scale)), rgba(0, 0, 0, 0.04) 0 calc(0.75rem * var(--mantine-scale)) calc(0.75rem * var(--mantine-scale)) calc(-0.4375rem * var(--mantine-scale))",
		// Custom shadows for GD
		xxs: "0px 2px 4px rgba(0, 0, 0, 0.1)",
		card: "0px 2px 8px rgba(0, 0, 0, 0.1)",
		button: "2px 2px 10px 1px rgba(79, 79, 79, 0.16)",
		elevated: "0px 4px 22px 0px rgba(0, 0, 0, 0.17)"
	},
	other: {
		sosTopHeight: rem(120),
		sosMaxHeight: rem(900),
		oneStopPricingMaxBoxSize: px(12),
		gradients: {
			headline: "linear-gradient(90deg, #052396 0%, #39BDD0 100%)",
			"title-background":
				"linear-gradient(0deg, #051557 0%, #223BA2 100%), #051557",
			"table-headline": "linear-gradient(91deg, #052396 6.83%, #39BDD0 98.27%)",
			"badge-background":
				"linear-gradient(251deg, #173E8D 2.02%, #2352A3 47.1%, #032B71 93.1%)",
			background:
				"linear-gradient(90deg, #173E8D 0%, #2352A3 50%, #032B71 100%)",
			"sos-background":
				"linear-gradient(247.18deg, rgba(77, 158, 217, 0.249) 25.64%, rgba(0, 49, 123, 0.83) 81.83%)"
		}
	}
})
